import { type FC } from "react";
import { Loader2Icon, CheckCircle2Icon, GitBranchIcon } from "lucide-react";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils";

interface StagingNode {
  id: string;
  name: string;
  label: string;
  grouping_behavior?: string;
  upstream_nodes: { id: string; name: string; label: string; node_type: string }[];
  downstream_entities: { id: string; name: string; label: string; node_type: string }[];
  columns: { name: string; type: string; part_of_composite_key?: boolean; merge_behavior?: string }[];
}

// Custom tool component for get_staging_nodes tool
const GetStagingNodesToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: unknown;
  status?: { type: "running" | "complete" | "incomplete"; reason?: string };
  args?: any;
  toolCallId?: string;
  [key: string]: any;
}> = ({ argsText, result, status }) => {
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const isError = status?.type === "incomplete" && status?.reason === "error";
  const isComplete = status?.type === "complete" && !isError;

  // Parse args for name filter
  let filterText = "";
  try {
    const parsedArgs = typeof argsText === 'string' ? JSON.parse(argsText) : argsText;
    if (parsedArgs?.name_filter) {
      filterText = ` matching "${parsedArgs.name_filter}"`;
    }
  } catch (e) {
    // Parsing failed
  }

  // Parse result to get staging node details
  let stagingNodes: StagingNode[] = [];
  if (result !== undefined && isComplete) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      stagingNodes = parsed?.staging_nodes || [];
    } catch (e) {
      // Parsing failed
    }
  }

  const mappedCount = stagingNodes.filter(n => n.downstream_entities.length > 0).length;
  const unmappedCount = stagingNodes.length - mappedCount;

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning ? "bg-primary/10" : "bg-muted/50"
      )}>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <>
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <span className="text-sm">Loading staging nodes{filterText}...</span>
            </>
          ) : isComplete ? (
            <>
              <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
              <span className="text-sm">
                Found {stagingNodes.length} staging node{stagingNodes.length !== 1 ? "s" : ""}{filterText}
                {stagingNodes.length > 0 && (
                  <span className="text-muted-foreground">
                    {" "}({mappedCount} mapped to entities{unmappedCount > 0 ? `, ${unmappedCount} unmapped` : ""})
                  </span>
                )}
              </span>
            </>
          ) : (
            <>
              <GitBranchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-muted-foreground">Querying staging nodes...</span>
            </>
          )}
        </div>
        {isComplete && stagingNodes.length > 0 && (
          <div className="mt-2 space-y-1 ml-6">
            {stagingNodes.map((node) => (
              <div key={node.id} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{node.label || node.name}</span>
                {node.upstream_nodes.length > 0 && (
                  <span> from {node.upstream_nodes.map(u => u.label || u.name).join(", ")}</span>
                )}
                {node.downstream_entities.length > 0 && (
                  <span> &rarr; {node.downstream_entities.map(d => d.label || d.name).join(", ")}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export const GetStagingNodesToolCall = GetStagingNodesToolCallComponent as React.ComponentType<any>;
